import React, { useMemo } from "react";
import PropTypes from "prop-types";

import { UIBulkActions } from "../../../ui";
import { getJoiningRequestBulkActions } from "./bulkActions";
import { joiningRequestStatuses } from "./constants";

const ApplicantsBulkActions = ({
  activeStatus,
  labels,
  selectedCount,
  selectedLabel,
  canAdd = true,
  canEdit = true,
  canDelete = true,
  canView = true,
  onApprove,
  onAccept,
  onReject,
  onUnassign,
  onAllocate,
  onExport,
  onEditRole,
  onRemove,
  onReactivate,
  onEditReason,
  onAddEditTag,
  onDeleteTag,
  isEditTagDisabled = false,
  isDeleteTagDisabled = false,
  applicantsActionsLocked = false,
}) => {
  const actions = useMemo(() => {
    const all = getJoiningRequestBulkActions(activeStatus, labels, {
      onApprove,
      onAccept,
      onReject,
      onUnassign,
      onAllocate,
      onExport,
      onEditRole,
      onRemove,
      onReactivate,
      onEditReason,
    });

    return all.filter((action) => {
      if (applicantsActionsLocked) {
        return action.key === "export" && canView;
      }

      switch (action.key) {
        case "approve":
        case "reject":
        case "unassign":
        case "allocate":
          return canAdd;
        case "accept":
        case "reactivate":
        case "editReason":
        case "editRole":
          return canEdit;
        case "export":
          return canView;
        case "remove":
          return canDelete;
        default:
          return canEdit || canAdd;
      }
    });
  }, [
    activeStatus,
    labels,
    canAdd,
    canEdit,
    canDelete,
    canView,
    onAccept,
    onAllocate,
    onExport,
    onApprove,
    onEditRole,
    onRemove,
    onReactivate,
    onEditReason,
    onUnassign,
    onReject,
    applicantsActionsLocked,
  ]);

  const menuItems = useMemo(() => {
    if (activeStatus !== joiningRequestStatuses.joined) {
      return [];
    }

    if (applicantsActionsLocked) {
      return canAdd
        ? [
            {
              key: "addEditTag",
              label: labels.addEditTag,
              disabled: isEditTagDisabled,
              onClick: onAddEditTag,
            },
          ]
        : [];
    }

    const items = [];
    if (canAdd) {
      items.push({
        key: "addEditTag",
        label: labels.addEditTag,
        disabled: isEditTagDisabled,
        onClick: onAddEditTag,
      });
      items.push({
        key: "deleteTag",
        label: labels.deleteTag,
        tone: "danger",
        disabled: isDeleteTagDisabled,
        onClick: onDeleteTag,
      });
    }
    return items;
  }, [
    activeStatus,
    applicantsActionsLocked,
    canAdd,
    labels.addEditTag,
    labels.deleteTag,
    isEditTagDisabled,
    isDeleteTagDisabled,
    onAddEditTag,
    onDeleteTag,
  ]);

  if (!actions.length && !menuItems.length) return null;

  return (
    <UIBulkActions
      actions={actions}
      selectedCount={selectedCount}
      selectedLabel={selectedLabel}
      menuItems={menuItems}
      className="event-applicants__bulk-actions"
    />
  );
};

ApplicantsBulkActions.propTypes = {
  activeStatus: PropTypes.string.isRequired,
  labels: PropTypes.object.isRequired,
  selectedCount: PropTypes.number.isRequired,
  selectedLabel: PropTypes.string.isRequired,
  canAdd: PropTypes.bool,
  canEdit: PropTypes.bool,
  canDelete: PropTypes.bool,
  canView: PropTypes.bool,
  onApprove: PropTypes.func.isRequired,
  onAccept: PropTypes.func.isRequired,
  onReject: PropTypes.func.isRequired,
  onUnassign: PropTypes.func.isRequired,
  onAllocate: PropTypes.func.isRequired,
  onExport: PropTypes.func,
  onEditRole: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  onReactivate: PropTypes.func,
  onEditReason: PropTypes.func,
  onAddEditTag: PropTypes.func.isRequired,
  onDeleteTag: PropTypes.func.isRequired,
  isEditTagDisabled: PropTypes.bool,
  isDeleteTagDisabled: PropTypes.bool,
  applicantsActionsLocked: PropTypes.bool,
};

ApplicantsBulkActions.defaultProps = {
  onExport: undefined,
  onReactivate: undefined,
  onEditReason: undefined,
  isEditTagDisabled: false,
  isDeleteTagDisabled: false,
  applicantsActionsLocked: false,
};

export default ApplicantsBulkActions;
